import { z } from "zod";

export const subjectCapabilitySchema = z.enum([
  "dashboard",
  "dictionary",
  "personalWords",
  "capture",
  "review",
  "questionBank",
  "questionPractice",
  "questionReview",
]);

export const subjectManifestSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  shortName: z.string().min(1),
  icon: z.string().min(1),
  enabled: z.boolean(),
  capabilities: z.array(subjectCapabilitySchema),
});
export type SubjectManifest = z.infer<typeof subjectManifestSchema>;

export const wordMembershipSchema = z.object({
  bookId: z.string(),
  code: z.string(),
  label: z.string(),
  kind: z.enum(["official", "variant", "phrase", "exam", "personal"]),
});

export const wordSenseSchema = z.object({
  id: z.string(),
  partOfSpeech: z.string().nullable(),
  definitionZh: z.string(),
  sortOrder: z.number().int().nonnegative(),
});
export type WordSense = z.infer<typeof wordSenseSchema>;

export interface WordPronunciation {
  headword: string;
  phoneticUk: string;
  phoneticUs: string;
}

export const PERSONAL_PARTS_OF_SPEECH = [
  "n.", "v.", "vi.", "vt.", "adj.", "adv.", "prep.", "pron.", "conj.", "num.",
  "art.", "aux.", "int.", "phr.", "phr.v.", "n.phr.", "adj.phr.", "adv.phr.",
  "prep.phr.", "conj.phr.",
] as const;
export const personalPartOfSpeechSchema = z.enum(PERSONAL_PARTS_OF_SPEECH);
export const personalSenseInputSchema = z.object({
  partOfSpeech: personalPartOfSpeechSchema,
  definitionZh: z.string().trim().min(1).max(600),
}).strict();
export type PersonalSenseInput = z.infer<typeof personalSenseInputSchema>;
export const coercePersonalSenses = (senses: Array<{ partOfSpeech: string | null; definitionZh: string | null }>): PersonalSenseInput[] => {
  const grouped = new Map<PersonalSenseInput["partOfSpeech"], string[]>();
  for (const sense of senses) {
    const partOfSpeech = personalPartOfSpeechSchema.safeParse(sense.partOfSpeech);
    const definition = sense.definitionZh?.trim();
    if (!partOfSpeech.success || !definition) continue;
    const definitions = grouped.get(partOfSpeech.data) ?? [];
    if (!definitions.includes(definition)) definitions.push(definition);
    grouped.set(partOfSpeech.data, definitions);
  }
  return [...grouped.entries()].slice(0, 8).map(([partOfSpeech, definitions]) => ({
    partOfSpeech,
    definitionZh: definitions.join("；").slice(0, 600),
  }));
};
export const personalSenseListSchema = z.array(personalSenseInputSchema).max(8).superRefine((senses, context) => {
  const seen = new Set<string>();
  senses.forEach((sense, index) => {
    if (seen.has(sense.partOfSpeech)) context.addIssue({
      code: "custom", path: [index, "partOfSpeech"], message: "Each part of speech can only have one row",
    });
    seen.add(sense.partOfSpeech);
  });
});

export const verificationPolicySchema = z.object({
  alphabet: z.enum(["numeric", "unambiguous_alphanumeric"]),
  length: z.union([z.literal(4), z.literal(6), z.literal(8)]),
  expiresMinutes: z.number().int().min(5).max(30),
  maxAttempts: z.number().int().min(3).max(10),
  resendCooldownSeconds: z.number().int().min(30).max(300),
});
export type VerificationPolicy = z.infer<typeof verificationPolicySchema>;

export const securityPolicySchema = z.object({
  registrationEnabled: z.boolean(),
  sessionDurationDays: z.number().int().min(1).max(365),
}).strict();
export type SecurityPolicy = z.infer<typeof securityPolicySchema>;

export const adminSecuritySettingsSchema = securityPolicySchema.extend({
  maintenanceEnabled: z.boolean(),
}).strict();
export type AdminSecuritySettings = z.infer<typeof adminSecuritySettingsSchema>;

export const adminDatabaseSettingsSchema = z.object({
  host: z.string().trim().min(1).max(255),
  port: z.number().int().min(1).max(65_535),
  user: z.string().trim().min(1).max(255),
  password: z.string().max(1000).optional(),
  database: z.string().trim().min(1).max(255),
  ssl: z.boolean(),
  poolSize: z.number().int().min(1).max(100),
}).strict();
export type AdminDatabaseSettings = z.infer<typeof adminDatabaseSettingsSchema>;

export const adminEmailSettingsSchema = z.object({
  host: z.string().trim().min(1).max(255),
  port: z.number().int().min(1).max(65_535),
  secure: z.boolean(),
  user: z.string().trim().min(1).max(320),
  password: z.string().max(1000).optional(),
  from: z.string().trim().min(1).max(320),
}).strict();
export type AdminEmailSettings = z.infer<typeof adminEmailSettingsSchema>;

export const adminAiSettingsSchema = z.object({
  baseUrl: z.url(),
  model: z.string().trim().min(1).max(120),
  apiKey: z.string().max(2000).optional(),
}).strict();
export type AdminAiSettings = z.infer<typeof adminAiSettingsSchema>;

export const adminStorageSettingsSchema = z.object({
  uploadDirectory: z.string().trim().min(1).max(1000),
  recognitionMaxMb: z.number().int().min(1).max(50),
  avatarMaxMb: z.number().int().min(1).max(20),
}).strict();
export type AdminStorageSettings = z.infer<typeof adminStorageSettingsSchema>;

export const adminStorageCleanupSchema = z.object({
  includeUnsaved: z.boolean(),
}).strict();
export type AdminStorageCleanup = z.infer<typeof adminStorageCleanupSchema>;

export interface AdminStorageCleanupSummary {
  fileCount: number;
  totalBytes: number;
  cleanableFiles: number;
  cleanableBytes: number;
  unsavedFiles: number;
  unsavedBytes: number;
  unsavedJobs: number;
  preservedJobs: number;
}

export interface AdminStorageCleanupResult {
  ok: true;
  deletedFiles: number;
  deletedBytes: number;
}

export const adminUserStatusSchema = z.enum(["active", "locked", "disabled"]);
export type AdminUserStatus = z.infer<typeof adminUserStatusSchema>;

export const adminUserUpdateSchema = z.object({
  email: z.string().transform((value) => value.trim().toLowerCase()).pipe(z.email().max(320)).optional(),
  displayName: z.string().trim().max(80).nullable().optional(),
  emailVerified: z.boolean().optional(),
  status: adminUserStatusSchema.optional(),
  admin: z.boolean().optional(),
}).strict().refine((value) => Object.values(value).some((item) => item !== undefined), {
  message: "At least one user change is required",
});
export type AdminUserUpdate = z.infer<typeof adminUserUpdateSchema>;

export interface AdminSettingsSnapshot {
  security: AdminSecuritySettings;
  verification: VerificationPolicy;
  database: Omit<AdminDatabaseSettings, "password"> & { passwordConfigured: boolean };
  email: Omit<AdminEmailSettings, "password"> & { passwordConfigured: boolean };
  ai: Omit<AdminAiSettings, "apiKey"> & { apiKeyConfigured: boolean };
  storage: AdminStorageSettings & {
    resolvedDirectory: string;
    writable: boolean;
    freeBytes: number | null;
    totalBytes: number | null;
    cleanup: AdminStorageCleanupSummary;
  };
}

export interface PublicConfig {
  registrationEnabled: boolean;
  maintenanceEnabled: boolean;
  uploadLimits: {
    recognitionMaxMb: number;
    avatarMaxMb: number;
  };
}

const normalizedEmailSchema = z.string()
  .transform((value) => value.trim().toLowerCase())
  .pipe(z.email());

export const registerSchema = z.object({
  email: normalizedEmailSchema,
  password: z.string().min(10).max(128),
});
export const verifyRegistrationSchema = registerSchema.extend({ code: z.string().min(4).max(8) });
export const loginSchema = registerSchema;
export const resetRequestSchema = z.object({ email: normalizedEmailSchema });
export const resetPasswordSchema = resetRequestSchema.extend({
  code: z.string().min(4).max(8),
  password: z.string().min(10).max(128),
});

export const profileUpdateSchema = z.object({
  displayName: z.string().trim().min(1).max(80),
});
export const studyReminderPreferenceSchema = z.object({
  enabled: z.boolean(),
  time: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/),
}).strict();
export const passwordChangeSchema = z.object({
  currentPassword: z.string().min(1).max(128),
  newPassword: z.string().min(10).max(128),
}).refine((value) => value.currentPassword !== value.newPassword, {
  path: ["newPassword"],
  message: "new_password_must_differ",
});
export const emailChangeRequestSchema = z.object({
  email: normalizedEmailSchema,
  currentPassword: z.string().min(1).max(128),
});
export const emailChangeConfirmSchema = z.object({
  email: normalizedEmailSchema,
  code: z.string().min(4).max(8),
});

export const manualWordSchema = z.object({
  headword: z.string().trim().min(1).max(80).regex(/^[A-Za-z][A-Za-z '\-]*$/),
  senses: personalSenseListSchema.default([]),
  notes: z.string().trim().max(1000).optional(),
  sourceSentence: z.string().trim().max(2000).optional(),
  examSentenceId: z.string().optional(),
  useAiCompletion: z.boolean().default(false),
});

export const personalWordUpdateSchema = z.object({
  senses: personalSenseListSchema.optional(),
  notes: z.string().trim().max(1000).nullable().optional(),
  sourceSentence: z.string().trim().max(2000).nullable().optional(),
}).strict().refine((value) => Object.keys(value).length > 0, {
  message: "At least one editable field is required",
});

export const personalWordBulkDeleteSchema = z.object({
  ids: z.array(z.uuid()).min(1).max(2000)
    .transform((ids) => [...new Set(ids)])
    .refine((ids) => ids.length <= 500, "At most 500 unique word ids may be deleted at once"),
}).strict();

export const recognitionCandidateSchema = z.object({
  id: z.string(),
  headword: z.string(),
  sourceSentence: z.string().nullable(),
  examSource: z.string().nullable(),
  confidence: z.number().min(0).max(1),
  selected: z.boolean(),
  senses: personalSenseListSchema,
  memberships: z.array(wordMembershipSchema),
  evidence: z.object({
    vision: z.number(),
    lexicon: z.number(),
    context: z.number(),
    frequency: z.number(),
  }),
});

export const recognitionCandidateUpdateSchema = z.object({
  headword: z.string().trim().min(1).max(80).regex(/^[A-Za-z][A-Za-z '\-]*$/).optional(),
  sourceSentence: z.string().trim().max(2000).nullable().optional(),
  selected: z.boolean().optional(),
  senses: personalSenseListSchema.refine((senses) => senses.length > 0).optional(),
}).strict().refine((value) => Object.keys(value).length > 0, { message: "At least one candidate change is required" });

export const recognitionCandidateSelectionSchema = z.object({
  selected: z.boolean(),
}).strict();

export const recognitionCandidateCreateSchema = z.object({
  headword: z.string().trim().min(1).max(80).regex(/^[A-Za-z][A-Za-z '\-]*$/),
  sourceSentence: z.string().trim().max(2000).optional(),
  examSentenceId: z.string().optional(),
  senses: personalSenseListSchema.default([]),
  useAiCompletion: z.boolean().default(true),
}).strict();

export const recognitionResultSchema = z.object({
  candidates: z.array(z.object({
    word: z.string().trim().min(1).max(80),
    alternatives: z.array(z.string().trim().min(1).max(80)).max(8).default([]),
    sentence: z.string().trim().max(2000).nullable().default(null),
    confidence: z.number().min(0).max(1),
    senses: personalSenseListSchema.refine((senses) => senses.length > 0 && senses.length <= 6),
  })),
});
export type RecognitionResultCandidate = z.infer<typeof recognitionResultSchema>["candidates"][number];

export const recognitionModeSchema = z.enum(["marked", "all_words"]);
export type RecognitionMode = z.infer<typeof recognitionModeSchema>;

export const jobStatusSchema = z.enum(["queued", "processing", "review_required", "completed", "failed"]);
export type JobStatus = z.infer<typeof jobStatusSchema>;

export const dailyReviewRatingSchema = z.enum(["again", "hard", "good"]);
export type DailyReviewRating = z.infer<typeof dailyReviewRatingSchema>;

export const studySourceSchema = z.enum(["personal_official", "personal_extra", "favorites", "kaoyan_frequency"]);
export type StudySource = z.infer<typeof studySourceSchema>;

export const newWordOrderSchema = z.enum(["recent", "alphabetical"]);
export type NewWordOrder = z.infer<typeof newWordOrderSchema>;
export const practiceWordOrderSchema = z.enum(["recent", "alphabetical", "random"]);
export type PracticeWordOrder = z.infer<typeof practiceWordOrderSchema>;

export const STUDY_TARGET_MIN = 1;
export const STUDY_TARGET_MAX = 100;
export const STUDY_INITIAL_BATCH_SIZE = 5;
export const PRACTICE_ROUND_MIN = 1;
export const PRACTICE_ROUND_MAX = 10;
export const DEFAULT_STUDY_SOURCES: StudySource[] = ["personal_official", "personal_extra"];

export const studyPreferencesSchema = z.object({
  sources: z.array(studySourceSchema).min(1).max(4)
    .default([...DEFAULT_STUDY_SOURCES]),
  spellingEnabled: z.boolean().default(true),
  newTaskSize: z.number().int().min(STUDY_TARGET_MIN).max(STUDY_TARGET_MAX).default(5),
  reviewTaskSize: z.number().int().min(STUDY_TARGET_MIN).max(STUDY_TARGET_MAX).default(20),
  newWordOrder: newWordOrderSchema.default("recent"),
}).strict();
export type StudyPreferences = z.infer<typeof studyPreferencesSchema>;
export const DEFAULT_STUDY_PREFERENCES: StudyPreferences = studyPreferencesSchema.parse({});

export const studyModeSchema = z.enum(["new_words", "review"]);
export type StudyMode = z.infer<typeof studyModeSchema>;

export const reviewScopeSchema = z.enum(["regular", "mistakes"]);
export type ReviewScope = z.infer<typeof reviewScopeSchema>;

export const studySessionRequestSchema = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal("new_words"),
    sources: studyPreferencesSchema.shape.sources,
    spellingEnabled: studyPreferencesSchema.shape.spellingEnabled,
    taskSize: studyPreferencesSchema.shape.newTaskSize,
    newWordOrder: studyPreferencesSchema.shape.newWordOrder,
  }).strict(),
  z.object({
    mode: z.literal("review"),
    reviewScope: reviewScopeSchema,
    sources: studyPreferencesSchema.shape.sources,
    spellingEnabled: studyPreferencesSchema.shape.spellingEnabled,
    taskSize: studyPreferencesSchema.shape.reviewTaskSize,
  }).strict(),
]);

export const practicePreferencesSchema = z.object({
  sources: z.array(studySourceSchema).min(1).max(4).default([...DEFAULT_STUDY_SOURCES]),
  spellingEnabled: z.boolean().default(true),
  count: z.number().int().min(STUDY_TARGET_MIN).max(STUDY_TARGET_MAX).default(20),
  order: practiceWordOrderSchema.default("recent"),
  roundCount: z.number().int().min(PRACTICE_ROUND_MIN).max(PRACTICE_ROUND_MAX).default(1),
}).strict();
export type PracticePreferences = z.infer<typeof practicePreferencesSchema>;
export const DEFAULT_PRACTICE_PREFERENCES: PracticePreferences = practicePreferencesSchema.parse({});

const studyAttemptBase = {
  sessionItemId: z.string().min(1),
  responseMs: z.number().int().nonnegative().max(600_000).optional(),
};
export const studyAttemptRequestSchema = z.discriminatedUnion("exercise", [
  z.object({
    ...studyAttemptBase,
    exercise: z.literal("introduction"),
    acknowledged: z.literal(true),
  }).strict(),
  z.object({
    ...studyAttemptBase,
    exercise: z.literal("recall"),
    remembered: z.boolean(),
  }).strict(),
  z.object({
    ...studyAttemptBase,
    exercise: z.literal("spelling"),
    answer: z.string().max(160),
  }).strict(),
]);
export type StudyAttemptRequest = z.infer<typeof studyAttemptRequestSchema>;

export const politicsQuestionTypeSchema = z.enum(["single_choice", "multiple_choice"]);
export type PoliticsQuestionType = z.infer<typeof politicsQuestionTypeSchema>;
export const politicsQuestionTypeFilterSchema = z.enum(["all", "single_choice", "multiple_choice"]);
export type PoliticsQuestionTypeFilter = z.infer<typeof politicsQuestionTypeFilterSchema>;
export const politicsOptionKeySchema = z.enum(["A", "B", "C", "D"]);
export type PoliticsOptionKey = z.infer<typeof politicsOptionKeySchema>;
export const politicsPracticeSourceSchema = z.enum(["bank", "section", "wrong", "favorites"]);
export type PoliticsPracticeSource = z.infer<typeof politicsPracticeSourceSchema>;
export const politicsQuestionOrderSchema = z.enum(["sequential", "random"]);
export type PoliticsQuestionOrder = z.infer<typeof politicsQuestionOrderSchema>;
export const politicsSessionStatusSchema = z.enum(["active", "completed", "abandoned"]);
export type PoliticsSessionStatus = z.infer<typeof politicsSessionStatusSchema>;

export const politicsSessionCreateSchema = z.object({
  clientRequestId: z.uuid(),
  bankId: z.uuid(),
  source: politicsPracticeSourceSchema,
  sectionId: z.uuid().optional(),
  questionType: politicsQuestionTypeFilterSchema.default("all"),
  order: politicsQuestionOrderSchema.default("sequential"),
  count: z.number().int().min(1).max(100).default(20),
  replaceActive: z.boolean().default(false),
}).strict().superRefine((value, context) => {
  if (value.source === "section" && !value.sectionId) {
    context.addIssue({
      code: "custom",
      path: ["sectionId"],
      message: "sectionId is required for section practice",
    });
  }
  if (value.source !== "section" && value.sectionId) {
    context.addIssue({
      code: "custom",
      path: ["sectionId"],
      message: "sectionId is only valid for section practice",
    });
  }
});
export type PoliticsSessionCreate = z.infer<typeof politicsSessionCreateSchema>;

export const politicsAnswerSchema = z.object({
  selectedOptionKeys: z.array(politicsOptionKeySchema).min(1).max(4)
    .refine((keys) => new Set(keys).size === keys.length, "Option keys must be unique"),
}).strict();
export type PoliticsAnswer = z.infer<typeof politicsAnswerSchema>;

export const politicsQuestionListQuerySchema = z.object({
  bankId: z.uuid().optional(),
  sectionId: z.uuid().optional(),
  questionType: politicsQuestionTypeFilterSchema.default("all"),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(50).default(20),
}).strict();
export type PoliticsQuestionListQuery = z.infer<typeof politicsQuestionListQuerySchema>;

export const politicsHistoryQuerySchema = z.object({
  status: z.enum(["all", "active", "completed", "abandoned"]).default("all"),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(50).default(20),
}).strict();
export type PoliticsHistoryQuery = z.infer<typeof politicsHistoryQuerySchema>;

export interface PoliticsQuestionOption {
  key: PoliticsOptionKey;
  text: string;
}

export interface PoliticsQuestionSectionSummary {
  id: string;
  parentId: string | null;
  code: string;
  name: string;
  sortOrder: number;
}

export interface PoliticsQuestionSectionTree extends PoliticsQuestionSectionSummary {
  questionCount: number;
  children: PoliticsQuestionSectionTree[];
}

export interface PoliticsQuestionBankSummary {
  id: string;
  code: string;
  name: string;
  edition: string | null;
  questionCount: number;
}

export interface PoliticsQuestionPrompt {
  id: string;
  type: PoliticsQuestionType;
  sourceNumber: number;
  stem: string;
  section: PoliticsQuestionSectionSummary;
  options: PoliticsQuestionOption[];
  favorite: boolean;
}

export interface PoliticsQuestionSolution {
  correctOptionKeys: PoliticsOptionKey[];
  explanation: string;
  reference: string | null;
  knowledgePoint: string | null;
  tip: string | null;
}

export interface PoliticsAnswerResult extends PoliticsQuestionSolution {
  correct: boolean;
  selectedOptionKeys: PoliticsOptionKey[];
}

export interface PoliticsSessionSummary {
  id: string;
  bankId: string;
  bankName: string;
  source: PoliticsPracticeSource;
  questionType: PoliticsQuestionTypeFilter;
  order: PoliticsQuestionOrder;
  status: PoliticsSessionStatus;
  requestedCount: number;
  actualCount: number;
  answeredCount: number;
  correctCount: number;
  wrongCount: number;
  startedAt: string;
  completedAt: string | null;
}

export interface PoliticsSessionItem {
  id: string;
  queueOrder: number;
  status: "queued" | "answered";
  question: PoliticsQuestionPrompt | null;
  result?: PoliticsAnswerResult;
}

export interface PoliticsSessionView extends PoliticsSessionSummary {
  currentItem: {
    id: string;
    queueOrder: number;
    question: PoliticsQuestionPrompt;
  } | null;
  items: PoliticsSessionItem[];
}

export interface PoliticsCourseProgress {
  id: string;
  code: string;
  name: string;
  totalQuestions: number;
  answeredQuestions: number;
  correctQuestions: number;
  accuracy: number;
}

export interface PoliticsDashboard {
  todayAnswered: number;
  todayCorrect: number;
  todayAccuracy: number;
  answeredQuestions: number;
  totalQuestions: number;
  wrongCount: number;
  favoriteCount: number;
  activeSession: PoliticsSessionSummary | null;
  courseProgress: PoliticsCourseProgress[];
}

export interface PoliticsQuestionProgressSummary {
  attemptCount: number;
  correctCount: number;
  wrongCount: number;
  lastResult: "correct" | "wrong" | null;
  lastAnsweredAt: string | null;
}

export interface PoliticsQuestionListItem {
  id: string;
  type: PoliticsQuestionType;
  sourceNumber: number;
  stem: string;
  section: { id: string; code: string; name: string };
  bank: { id: string; name: string };
  favorite: boolean;
  progress: PoliticsQuestionProgressSummary;
}

export interface PoliticsQuestionDetail extends PoliticsQuestionPrompt {
  attempted: boolean;
  progress: PoliticsQuestionProgressSummary & { wrongActive: boolean };
  solution: PoliticsQuestionSolution | null;
}

export const importManifestSchema = z.object({
  sourceName: z.string(),
  sourceUrl: z.url().optional(),
  sha256: z.string().length(64),
  sourceRows: z.number().int().nonnegative(),
  normalizedEntries: z.number().int().nonnegative(),
  duplicateEntries: z.array(z.string()),
  phrases: z.array(z.string()),
  alphabetCoverage: z.array(z.string()),
  authority: z.enum(["official", "carrier", "lexical_enrichment", "exam_corpus"]),
});

export const importDiffReportSchema = z.object({
  added: z.array(z.string()),
  removed: z.array(z.string()),
  unresolved: z.array(z.string()),
  publishable: z.boolean(),
});

export const ENGLISH_SUBJECT: SubjectManifest = {
  id: "english",
  name: "考研英语",
  shortName: "英语",
  icon: "book-open",
  enabled: true,
  capabilities: ["dashboard", "dictionary", "personalWords", "capture", "review"],
};

export const POLITICS_SUBJECT: SubjectManifest = {
  id: "politics",
  name: "考研政治",
  shortName: "政治",
  icon: "landmark",
  enabled: true,
  capabilities: ["dashboard", "questionBank", "questionPractice", "questionReview"],
};
